import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ─────────────────────────────────────────────────────────────────────────────
// Auth token storage
// ─────────────────────────────────────────────────────────────────────────────
// SECURITY NOTE (Jul 2026 audit):
//   The JWT is stored in localStorage. This is a documented, accepted risk:
//     • XSS surface: any successful XSS could exfiltrate the token.
//     • Mitigation: strict CSP + input sanitization + JWT is short-lived.
//   Alternative: httpOnly cookies. Deferred — requires backend cookie-set logic,
//   CSRF protection, and forces every existing user to re-authenticate. Tracked
//   in plan.md → "Code Quality Backlog / Deferred".
// All auth-token reads MUST go through getToken(); never read TOKEN_KEY directly
// from localStorage in component code — this keeps the migration path clean.
const TOKEN_KEY = 'billlens_token';

export function getToken() {
    return localStorage.getItem(TOKEN_KEY) || null;
}
export function setToken(t) {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
}

const client = axios.create({ baseURL: API });

client.interceptors.request.use((config) => {
    const t = getToken();
    if (t) config.headers.Authorization = `Bearer ${t}`;
    return config;
});

// Global 401 handler — redirect to /login (bypass on auth endpoints themselves).
// Also global 402 handler — for locked/past_due tenants, surface a toast + push
// the user to the Billing section so they can subscribe. Emits a custom event
// `billlens:entitlement-blocked` that App-level components can hook into.
client.interceptors.response.use(
    (r) => r,
    (err) => {
        const url = (err?.config?.url || '').toString();
        const status = err?.response?.status;
        if (status === 401 && !url.includes('/auth/')) {
            setToken(null);
            const path = window.location.pathname + window.location.search;
            if (!window.location.pathname.startsWith('/login')) {
                window.location.replace(`/login?next=${encodeURIComponent(path)}`);
            }
        } else if (status === 402) {
            // Payment Required — trial/grace period is over.
            // The detail body carries { code, message, state, trial_end, grace_end }.
            const detail = err?.response?.data?.detail || {};
            try {
                window.dispatchEvent(new CustomEvent('billlens:entitlement-blocked', {
                    detail: {
                        message: detail.message || 'Your access is locked. Please subscribe to continue.',
                        state: detail.state || 'locked',
                        trialEnd: detail.trial_end || null,
                        graceEnd: detail.grace_end || null,
                        sourceUrl: url,
                    },
                }));
            } catch { /* noop */ }
        }
        return Promise.reject(err);
    }
);

/**
 * Trigger a browser download from an axios response whose data is a Blob.
 * Extracts the filename from Content-Disposition, or falls back to the provided default.
 */
function _saveBlobFromResponse(r, defaultFilename) {
    const disp = r.headers?.['content-disposition'] || '';
    const m = /filename="?([^"]+)"?/i.exec(disp);
    const filename = m ? m[1] : defaultFilename;
    const blob = new Blob([r.data], { type: r.headers?.['content-type'] || 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    return { filename, size: blob.size };
}

export const api = {
    // ---- Auth ----
    authStatus: () => client.get('/auth/status').then((r) => r.data),
    // Legacy single-user setup — kept for backward compat; new flow is
    // registerStart + registerVerify (multi-tenant OTP).
    setup: (email, password) => client.post('/auth/setup', { email, password }).then((r) => r.data),
    registerStart: (email, password) =>
        client.post('/auth/register/start', { email, password }).then((r) => r.data),
    registerResend: (email) =>
        client.post('/auth/register/resend', { email }).then((r) => r.data),
    registerVerify: (email, otp) =>
        client.post('/auth/register/verify', { email, otp }).then((r) => r.data),
    login: (email, password) => client.post('/auth/login', { email, password }).then((r) => r.data),
    // Google Sign-In via Emergent Auth. Exchanges the session_id we
    // received in the URL fragment for our regular JWT + user shape.
    googleSession: (session_id) =>
        client.post('/auth/google-session', { session_id }).then((r) => r.data),
    me: () => client.get('/auth/me').then((r) => r.data),
    forgotPassword: (email) => client.post('/auth/forgot-password', { email }).then((r) => r.data),
    // 2026-01: OTP-based forgot password (no recovery code needed).
    forgotStart: (email) => client.post('/auth/forgot-password/start', { email }).then((r) => r.data),
    forgotVerify: (email, otp, new_password) =>
        client
            .post('/auth/forgot-password/verify', { email, otp, new_password })
            .then((r) => r.data),
    resetWithToken: (token, new_password) =>
        client.post('/auth/reset-password/token', { token, new_password }).then((r) => r.data),
    resetWithCode: (email, recovery_code, new_password) =>
        client
            .post('/auth/reset-password/code', { email, recovery_code, new_password })
            .then((r) => r.data),
    resetWorkspace: (email, recovery_code) =>
        client
            .post('/auth/reset-workspace', { email, recovery_code })
            .then((r) => r.data),
    changePassword: (current_password, new_password) =>
        client.post('/auth/change-password', { current_password, new_password }).then((r) => r.data),
    // ---- Delete-my-data (Sprint C) ----
    deleteAccountInit: () =>
        client.post('/account/delete/init', {}).then((r) => r.data),
    deleteAccountConfirm: ({ otp, password }) =>
        client.post('/account/delete/confirm', { otp, password }).then((r) => r.data),

    // ---- Domain ----
    stats: (params = {}) => client.get('/stats', { params }).then((r) => r.data),
    listBills: (params = {}) => client.get('/bills', { params }).then((r) => r.data),
    // Sprint 2026-07: rescan an existing bill (re-runs OCR on the stored file).
    rescanBill: (billId) =>
        client.post(`/bills/${encodeURIComponent(billId)}/rescan`).then((r) => r.data),
    // Sprint 2026-08: bulk rescan by vendor (or explicit bill IDs).
    // Used after the Claude Sonnet 5 swap to reprocess older bills that
    // were saved with less-accurate historical OCR extractions.
    bulkRescanBills: (payload) =>
        client.post('/bills/bulk-rescan', payload).then((r) => r.data),
    // Sprint 2026-07 (B): review-queue helpers.
    billsReviewCount: () => client.get('/bills/review-count').then((r) => r.data),
    markBillReviewed: (billId) =>
        client.post(`/bills/${encodeURIComponent(billId)}/mark-reviewed`).then((r) => r.data),
    markBillsReviewedBulk: (billIds) =>
        client.post('/bills/mark-reviewed-bulk', { bill_ids: billIds }).then((r) => r.data),
    // ---- Owner / Super-Admin (Sprint 2026-01) ----
    ownerWhoami: () => client.get('/owner/whoami').then((r) => r.data),
    // ---- Scanner allowlist (Daily Sheet + Vijay Ras) ----
    // Superset of ownerWhoami: OWNER_EMAIL is always in the allowlist, but
    // OWNER_SCANNER_EMAILS can add trusted family/staff/test accounts too.
    scannerWhoami: () => client.get('/scanner/whoami').then((r) => r.data),
    ownerOverview: () => client.get('/owner/overview').then((r) => r.data),
    ownerTenants: (params = {}) => client.get('/owner/tenants', { params }).then((r) => r.data),
    // Trust Sprint: every call is audited. `reason` is optional but required
    // to unmask sensitive fields. Server records who/when/why.
    ownerTenantDetail: (email, { reason, unmask } = {}) => {
        const params = {};
        if (reason) params.reason = reason;
        if (unmask) params.unmask = true;
        return client.get(`/owner/tenant/${encodeURIComponent(email)}`, { params }).then((r) => r.data);
    },
    // Trust Sprint: tenant-facing view into the super-admin audit log.
    accountAccessLog: (limit = 50) =>
        client.get('/account/access-log', { params: { limit } }).then((r) => r.data),

    getBill: (id) => client.get(`/bills/${id}`).then((r) => r.data),
    deleteBill: (id) => client.delete(`/bills/${id}`).then((r) => r.data),
    updateBill: (id, body) => client.patch(`/bills/${id}`, body).then((r) => r.data),
    uploadBill: (formData, onUploadProgress, opts = {}) =>
        client
            .post('/bills/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress,
                timeout: 120000,
                // 200-299 = success, 409 = duplicate, 422 = classifier rejection (Phase 14)
                validateStatus: (s) => (s >= 200 && s < 300) || s === 409 || s === 422,
                ...opts,
            })
            .then((r) => ({ status: r.status, data: r.data })),
    // Sprint 2026-08 · Manual bill entry (no OCR)
    createManualBill: (body) =>
        client
            .post('/bills/manual', body, {
                validateStatus: (s) => (s >= 200 && s < 300) || s === 409,
            })
            .then((r) => ({ status: r.status, data: r.data })),
    uploadMultipageBill: (formData, onUploadProgress, opts = {}) =>
        client
            .post('/bills/upload/multipage', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress,
                timeout: 180000,
                validateStatus: (s) => (s >= 200 && s < 300) || s === 409 || s === 422,
                ...opts,
            })
            .then((r) => ({ status: r.status, data: r.data })),
    // ---- Multi-invoice PDF preview (Phase 11) ----
    getBillPreview: (preview_id) =>
        client.get(`/bills/preview/${encodeURIComponent(preview_id)}`).then((r) => r.data),
    discardBillPreview: (preview_id) =>
        client.delete(`/bills/preview/${encodeURIComponent(preview_id)}`).then((r) => r.data),
    commitBillPreview: (body) =>
        client.post('/bills/commit', body).then((r) => r.data),
    listAlerts: (params = {}) => client.get('/alerts', { params }).then((r) => r.data),
    dismissAlert: (id) => client.post(`/alerts/${id}/dismiss`).then((r) => r.data),
    restoreAlert: (id) => client.post(`/alerts/${id}/restore`).then((r) => r.data),
    dismissVisibleAlerts: (params = {}) =>
        client.post('/alerts/dismiss-visible', null, { params }).then((r) => r.data),

    // ---- Expenses (Phase 17) ----
    listExpenseCategories: () =>
        client.get('/expense-categories').then((r) => r.data),
    addExpenseCategory: (body) =>
        client.post('/expense-categories', body).then((r) => r.data),
    removeExpenseCategory: (key) =>
        client.delete(`/expense-categories/${encodeURIComponent(key)}`).then((r) => r.data),
    listExpenses: (params = {}) => {
        // The MonthPicker's "All time" option emits the sentinel value
        // 'all'. Backend regex only accepts YYYY-MM, so we translate that
        // to an omitted param here — this way every caller stays clean.
        const p = { ...params };
        if (p.month === 'all' || p.month === '') delete p.month;
        return client.get('/expenses', { params: p }).then((r) => r.data);
    },
    createExpense: (body) => client.post('/expenses', body).then((r) => r.data),
    updateExpense: (id, body) =>
        client.patch(`/expenses/${id}`, body).then((r) => r.data),
    confirmExpense: (id) =>
        client.post(`/expenses/${id}/confirm`).then((r) => r.data),
    deleteExpense: (id) => client.delete(`/expenses/${id}`).then((r) => r.data),
    listRecurring: () => client.get('/recurring').then((r) => r.data),
    createRecurring: (body) => client.post('/recurring', body).then((r) => r.data),
    updateRecurring: (id, body) =>
        client.patch(`/recurring/${id}`, body).then((r) => r.data),
    deleteRecurring: (id) => client.delete(`/recurring/${id}`).then((r) => r.data),
    runRecurringNow: () => client.post('/recurring/run-now').then((r) => r.data),

    // ---- Workspace / Business identity (Phase 16) ----
    getWorkspace: () => client.get('/workspace').then((r) => r.data),
    saveWorkspace: (body) => client.post('/workspace', body).then((r) => r.data),
    addWorkspaceGstin: (gstin) =>
        client.post('/workspace/gstins', { gstin }).then((r) => r.data),
    removeWorkspaceGstin: (gstin) =>
        client
            .delete(`/workspace/gstins/${encodeURIComponent(gstin)}`)
            .then((r) => r.data),

    listVendors: (params = {}) => client.get('/vendors', { params }).then((r) => r.data),
    getVendor: (vendor_key) =>
        client.get(`/vendors/${encodeURIComponent(vendor_key)}`).then((r) => r.data),
    renameVendor: (source_key, body) =>
        client
            .post(`/vendors/${encodeURIComponent(source_key)}/rename`, body)
            .then((r) => r.data),

    // ---- Vendor payments (Phase 6) ----
    getVendorBalance: (vendor_key, params = {}) =>
        client
            .get(`/vendors/${encodeURIComponent(vendor_key)}/balance`, { params })
            .then((r) => r.data),
    listVendorPayments: (vendor_key, params = {}) =>
        client
            .get(`/vendors/${encodeURIComponent(vendor_key)}/payments`, { params })
            .then((r) => r.data),
    createVendorPayment: (vendor_key, body) =>
        client
            .post(`/vendors/${encodeURIComponent(vendor_key)}/payments`, body)
            .then((r) => r.data),
    deleteVendorPayment: (payment_id) =>
        client.delete(`/vendor-payments/${encodeURIComponent(payment_id)}`).then((r) => r.data),
    getVendorMeta: (vendor_key) =>
        client.get(`/vendors/${encodeURIComponent(vendor_key)}/meta`).then((r) => r.data),
    updateVendorMeta: (vendor_key, body) =>
        client
            .patch(`/vendors/${encodeURIComponent(vendor_key)}/meta`, body)
            .then((r) => r.data),

    // ---- Data backup & restore (persistence) ----
    /** Streams a JSON download of the full workspace. Returns { blob, filename }. */
    exportData: async () => {
        const r = await client.get('/data/export', { responseType: 'blob' });
        // Attempt to grab the server-suggested filename
        const disp = r.headers?.['content-disposition'] || '';
        const m = /filename="?([^"]+)"?/i.exec(disp);
        const filename = m ? m[1] : `billlens-backup-${Date.now()}.json`;
        return { blob: r.data, filename };
    },
    importData: (payload, mode = 'replace') =>
        client.post('/data/import', { ...payload, mode }).then((r) => r.data),

    // ---- CA Export (Phase 10) ----
    caExportPreview: (month) =>
        client.get('/ca-export/preview', { params: { month } }).then((r) => r.data),
    async caExportLedger(month) {
        const r = await client.get('/ca-export/ledger', {
            params: { month },
            responseType: 'blob',
        });
        return _saveBlobFromResponse(r, `EaseMyBill_CA_Ledger_${month}.zip`);
    },
    async caExportGstSummary(month) {
        const r = await client.get('/ca-export/gst-summary.pdf', {
            params: { month },
            responseType: 'blob',
        });
        return _saveBlobFromResponse(r, `EaseMyBill_GST_Summary_${month}.pdf`);
    },
    async caExportVendorStatements(month) {
        const r = await client.get('/ca-export/vendor-statements.pdf', {
            params: { month },
            responseType: 'blob',
        });
        return _saveBlobFromResponse(r, `EaseMyBill_Vendor_Statements_${month}.pdf`);
    },
    async caExportExcel(month) {
        const r = await client.get('/ca-export/workbook.xlsx', {
            params: { month },
            responseType: 'blob',
        });
        return _saveBlobFromResponse(r, `EaseMyBill_Report_${month}.xlsx`);
    },
    async caExportTally(month) {
        const r = await client.get('/ca-export/tally.xml', {
            params: { month },
            responseType: 'blob',
        });
        return _saveBlobFromResponse(r, `EaseMyBill_Tally_${month}.xml`);
    },

    // ---- Billing (Razorpay Subscriptions) ----
    getBillingStatus: () => client.get('/billing/status').then((r) => r.data),
    createSubscription: () =>
        client.post('/billing/subscription/create').then((r) => r.data),
    cancelSubscription: (atCycleEnd = true) =>
        client.post('/billing/subscription/cancel', { at_cycle_end: atCycleEnd }).then((r) => r.data),
    listInvoices: () => client.get('/billing/invoices').then((r) => r.data),

    // ---- WhatsApp (Phase 12) ----
    getWhatsappStatus: () => client.get('/whatsapp/status').then((r) => r.data),
    linkWhatsappPhone: (phone) =>
        client.post('/whatsapp/link-phone', { phone }).then((r) => r.data),
    verifyWhatsappOtp: (code) =>
        client.post('/whatsapp/verify-otp', { code }).then((r) => r.data),
    getWhatsappOtpStatus: () => client.get('/whatsapp/otp-status').then((r) => r.data),
    cancelWhatsappOtp: () => client.post('/whatsapp/cancel-otp').then((r) => r.data),
    unlinkWhatsappPhone: () => client.delete('/whatsapp/link-phone').then((r) => r.data),
    whatsappTestSend: () => client.post('/whatsapp/test-send').then((r) => r.data),

    // ---- Vendor Ledger Reconciliation (Phase 13) ----
    reconcileVendor: (vendorKey, file, _model = 'claude-sonnet-5') => {
        const fd = new FormData();
        fd.append('file', file);
        return client
            .post(`/vendors/${encodeURIComponent(vendorKey)}/reconcile`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
                params: { model: 'claude-sonnet-5' },
                timeout: 180000, // ledgers are big; give Claude Sonnet 5 up to 3 min
            })
            .then((r) => r.data);
    },
    resolvePaymentDelta: (vendorKey, choice) =>
        client
            .post(`/vendors/${encodeURIComponent(vendorKey)}/reconciliation/resolve`, { choice })
            .then((r) => r.data),
    getVendorReconciliation: (vendorKey) =>
        client
            .get(`/vendors/${encodeURIComponent(vendorKey)}/reconciliation`)
            .then((r) => r.data),
    deleteVendorReconciliation: (vendorKey) =>
        client
            .delete(`/vendors/${encodeURIComponent(vendorKey)}/reconciliation`)
            .then((r) => r.data),
    listReconciliations: () => client.get('/reconciliations').then((r) => r.data),
    getReconciliationNudge: () => client.get('/reconciliation/nudge').then((r) => r.data),

    // ---- Digital Library (Sprint 2026-09) ----
    // Returns bills + expenses + ledger PDFs in one call so the Library
    // page can render both "By Vendor" and "By Month" divider views from
    // a single response. Params mirror /library on the backend.
    getLibrary: (params = {}) =>
        client.get('/library', { params }).then((r) => r.data),

    // Duplicate detection — returns groups of bills that appear to be
    // the same physical invoice (matched by file hash, invoice #, or
    // vendor + date + total). The frontend renders each group and lets
    // the user choose which copy to keep.
    findDuplicates: () => client.get('/duplicates').then((r) => r.data),

    // Bulk-delete a list of bill IDs (used by the duplicate cleanup UI).
    bulkDeleteBills: (ids) =>
        client.post('/bills/bulk-delete', { ids }).then((r) => r.data),

    // Merge all files matching the given filter into ONE PDF and stream
    // it back. Used by the "Export folder" button so a CA can be handed
    // the whole file cabinet in a single download.
    exportLibraryFolder: (payload = {}) =>
        client
            .post('/library/export', payload, {
                responseType: 'blob',
                timeout: 180000,
            })
            .then((r) => r),

    // Sprint 2026-09: separate-files download. Same filter shape as
    // exportLibraryFolder but streams a ZIP where every bill/ledger is
    // an individually-named file (YYYY-MM-DD_INITIALS_INV#.ext).
    exportLibraryZip: (payload = {}) =>
        client
            .post('/library/export-zip', payload, {
                responseType: 'blob',
                timeout: 180000,
            })
            .then((r) => r),

    // ---- Uploaded ledger history per vendor (2026-07) ----
    listVendorLedgerFiles: (vendorKey) =>
        client
            .get(`/vendors/${encodeURIComponent(vendorKey)}/ledgers`)
            .then((r) => r.data),
    vendorLedgerFileDownloadUrl: (vendorKey, ledgerId) =>
        `${client.defaults.baseURL}/vendors/${encodeURIComponent(vendorKey)}/ledgers/${encodeURIComponent(ledgerId)}/file`,
    downloadVendorLedgerFile: (vendorKey, ledgerId) =>
        client
            .get(
                `/vendors/${encodeURIComponent(vendorKey)}/ledgers/${encodeURIComponent(ledgerId)}/file`,
                { responseType: 'blob' },
            )
            .then((r) => r),
    deleteVendorLedgerFile: (vendorKey, ledgerId) =>
        client
            .delete(`/vendors/${encodeURIComponent(vendorKey)}/ledgers/${encodeURIComponent(ledgerId)}`)
            .then((r) => r.data),

    // ---- Staff salary (Phase 8) ----
    listStaff: (params = {}) => client.get('/staff', { params }).then((r) => r.data),
    createStaff: (body, params = {}) =>
        client.post('/staff', body, { params }).then((r) => r.data),
    getStaff: (id, params = {}) =>
        client.get(`/staff/${encodeURIComponent(id)}`, { params }).then((r) => r.data),
    updateStaff: (id, body) =>
        client.patch(`/staff/${encodeURIComponent(id)}`, body).then((r) => r.data),
    deleteStaff: (id) =>
        client.delete(`/staff/${encodeURIComponent(id)}`).then((r) => r.data),
    getStaffMonth: (id, month) =>
        client
            .get(`/staff/${encodeURIComponent(id)}/months/${encodeURIComponent(month)}`)
            .then((r) => r.data),
    updateStaffMonth: (id, month, body) =>
        client
            .patch(`/staff/${encodeURIComponent(id)}/months/${encodeURIComponent(month)}`, body)
            .then((r) => r.data),
    listStaffTransactions: (id, params = {}) =>
        client
            .get(`/staff/${encodeURIComponent(id)}/transactions`, { params })
            .then((r) => r.data),
    createStaffTransaction: (id, body) =>
        client
            .post(`/staff/${encodeURIComponent(id)}/transactions`, body)
            .then((r) => r.data),
    deleteStaffTransaction: (txn_id) =>
        client
            .delete(`/staff-transactions/${encodeURIComponent(txn_id)}`)
            .then((r) => r.data),
    getStaffSummary: (id, params = {}) =>
        client
            .get(`/staff/${encodeURIComponent(id)}/summary`, { params })
            .then((r) => r.data),
    getStaffBonusLedger: (params = {}) =>
        client.get('/staff/bonus-ledger', { params }).then((r) => r.data),
    listItems: (params = {}) => client.get('/items', { params }).then((r) => r.data),
    itemHistory: (vendor_key, item_key) =>
        client.get('/items/history', { params: { vendor_key, item_key } }).then((r) => r.data),
    mergeItems: (body) => client.post('/items/merge', body).then((r) => r.data),
    // 2026-07: Rename replaces the merge UX. When two items end up with the
    // same normalized name after rename, they auto-aggregate as one.
    renameItem: (body) => client.post('/items/rename', body).then((r) => r.data),
    getSettings: () => client.get('/settings').then((r) => r.data),
    updateSettings: (body) => client.post('/settings', body).then((r) => r.data),
    testEmail: () => client.post('/settings/test-email').then((r) => r.data),

    // ---- File download ----
    async downloadBillFile(bill_id, filename = 'bill') {
        const resp = await client.get(`/bills/${bill_id}/file`, { responseType: 'blob' });
        const blob = new Blob([resp.data]);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // try to extract from content-disposition
        const cd = resp.headers?.['content-disposition'] || '';
        const m = /filename="?([^"]+)"?/i.exec(cd);
        a.download = m ? m[1] : filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 4000);
    },

    // ================================================================
    // Invoicing (Phase 19) — Sales-side invoices
    // ================================================================
    // Customers
    invListCustomers: (params = {}) =>
        client.get('/invoicing/customers', { params }).then((r) => r.data),
    invCreateCustomer: (body) =>
        client.post('/invoicing/customers', body).then((r) => r.data),
    invGetCustomer: (id) =>
        client.get(`/invoicing/customers/${encodeURIComponent(id)}`).then((r) => r.data),
    invUpdateCustomer: (id, body) =>
        client.patch(`/invoicing/customers/${encodeURIComponent(id)}`, body).then((r) => r.data),
    invDeleteCustomer: (id) =>
        client.delete(`/invoicing/customers/${encodeURIComponent(id)}`).then((r) => r.data),
    invCustomerStatement: (id) =>
        client.get(`/invoicing/customers/${encodeURIComponent(id)}/statement`).then((r) => r.data),

    // Invoices
    invSummary: () => client.get('/invoicing/invoices/summary').then((r) => r.data),
    invListInvoices: (params = {}) =>
        client.get('/invoicing/invoices', { params }).then((r) => r.data),
    invCreateInvoice: (body) =>
        client.post('/invoicing/invoices', body).then((r) => r.data),
    invGetInvoice: (id) =>
        client.get(`/invoicing/invoices/${encodeURIComponent(id)}`).then((r) => r.data),
    invUpdateInvoice: (id, body) =>
        client.patch(`/invoicing/invoices/${encodeURIComponent(id)}`, body).then((r) => r.data),
    invIssueInvoice: (id) =>
        client.post(`/invoicing/invoices/${encodeURIComponent(id)}/issue`, {}).then((r) => r.data),
    invCancelInvoice: (id, reason) =>
        client.post(`/invoicing/invoices/${encodeURIComponent(id)}/cancel`, { reason }).then((r) => r.data),
    invDeleteInvoice: (id) =>
        client.delete(`/invoicing/invoices/${encodeURIComponent(id)}`).then((r) => r.data),
    invDuplicateInvoice: (id) =>
        client.post(`/invoicing/invoices/${encodeURIComponent(id)}/duplicate`).then((r) => r.data),
    invInvoicePdfUrl: (id) =>
        `${client.defaults.baseURL}/invoicing/invoices/${encodeURIComponent(id)}/pdf`,
    // Fetches the invoice PDF as a Blob using the central axios client (auth attached automatically).
    // Prefer this over building a fetch() call with a manually attached token — keeps token access
    // encapsulated in api.js and simplifies future migration away from localStorage.
    invInvoicePdfBlob: (id) =>
        client
            .get(`/invoicing/invoices/${encodeURIComponent(id)}/pdf`, { responseType: 'blob' })
            .then((r) => r.data),
    // 2026-07: Owner-only super-admin action. Whitelist an email for
    // unlimited scans (or revoke). Also provisions a new account when
    // create_if_missing=true.
    ownerGrantUnlimited: (body) =>
        client.post('/owner/grant-unlimited', body).then((r) => r.data),
    // 2026-07: Owner-only. One-off retroactive fix for bills where OCR
    // missed the tax lines and dumped the amount into round-off.
    ownerBackfillTax: (body = {}) =>
        client.post('/owner/backfill-tax', body).then((r) => r.data),
    // 2026-07: Scan top-up packs (one-time Razorpay orders).
    listScanPacks: () => client.get('/billing/scan-packs').then((r) => r.data),
    purchaseScanPack: (pack_code) =>
        client.post('/billing/scan-packs/purchase', { pack_code }).then((r) => r.data),
    // 2026-07: Founding 100 promo live counter (public, unauth-friendly).
    foundingStatus: () => client.get('/billing/founding-status').then((r) => r.data),

    // Payments
    invListPayments: (params = {}) =>
        client.get('/invoicing/payments', { params }).then((r) => r.data),
    invCreatePayment: (body) =>
        client.post('/invoicing/payments', body).then((r) => r.data),
    invDeletePayment: (id) =>
        client.delete(`/invoicing/payments/${encodeURIComponent(id)}`).then((r) => r.data),

    // ---- Sprint 2026-07: sharing + bulk actions ----
    // Send the invoice PDF via SMTP (workspace-level SMTP config).
    invEmailInvoice: (id, body) =>
        client.post(`/invoicing/invoices/${encodeURIComponent(id)}/email`, body).then((r) => r.data),
    // Generate (or reuse) a Razorpay Payment Link for the invoice.
    invCreatePaymentLink: (id) =>
        client.post(`/invoicing/invoices/${encodeURIComponent(id)}/payment-link`).then((r) => r.data),
    // Apply one action (mark_paid | delete | cancel) to many invoices.
    invBulkAction: (action, ids) =>
        client.post('/invoicing/invoices/bulk-action', { action, ids }).then((r) => r.data),

    // ---- Sprint 2 (Quotes + CN + DN + item catalog) ----
    // Doc-type-generic list/create. Under the hood these all use the same
    // sales_invoices collection with a `doc_type` discriminator.
    invListDocs: (docType, params = {}) => {
        // docType = 'quote' | 'credit_note' | 'debit_note' | 'delivery_challan' | 'sales_order' | 'purchase_order'
        const pathMap = {
            quote: 'quotes',
            credit_note: 'credit-notes',
            debit_note: 'debit-notes',
            delivery_challan: 'delivery-challans',
            sales_order: 'sales-orders',
            purchase_order: 'purchase-orders',
        };
        return client
            .get(`/invoicing/${pathMap[docType]}`, { params })
            .then((r) => r.data);
    },
    invCreateDoc: (docType, body) => {
        const pathMap = {
            quote: 'quotes',
            credit_note: 'credit-notes',
            debit_note: 'debit-notes',
            delivery_challan: 'delivery-challans',
            sales_order: 'sales-orders',
            purchase_order: 'purchase-orders',
        };
        return client
            .post(`/invoicing/${pathMap[docType]}`, body)
            .then((r) => r.data);
    },
    // GSTR filing helpers (Sprint 2026-09)
    invGSTR1: (month, format = 'summary') =>
        client
            .get('/invoicing/reports/gstr1', { params: { month, format } })
            .then((r) => r.data),
    invGSTR3B: (month) =>
        client
            .get('/invoicing/reports/gstr3b', { params: { month } })
            .then((r) => r.data),
    // Tally XML export (Sprint 2026-09). Returns the raw response so the
    // caller can extract Content-Disposition + X-Tally-* headers.
    invTallyExport: (params = {}) =>
        client
            .get('/invoicing/exports/tally', {
                params,
                responseType: 'blob',
                timeout: 180000,
            })
            .then((r) => r),
    // Thermal ESC/POS payload for a single invoice.
    invThermalPayload: (invoiceId, paper = 80) =>
        client
            .get(`/invoicing/invoices/${encodeURIComponent(invoiceId)}/thermal`, {
                params: { paper, encoding: 'base64' },
            })
            .then((r) => r.data),
    // Convert a quote → draft invoice. Cross-links both.
    invConvertQuote: (qid) =>
        client
            .post(`/invoicing/quotes/${encodeURIComponent(qid)}/convert-to-invoice`)
            .then((r) => r.data),
    // Item autocomplete from historical line items.
    invSuggestItems: (q, limit = 8) =>
        client
            .get('/invoicing/items/suggest', { params: { q, limit } })
            .then((r) => r.data.items || []),

    // Reports
    invAgingReport: () =>
        client.get('/invoicing/reports/receivables-aging').then((r) => r.data),
    invSalesTrend: (months = 6) =>
        client.get('/invoicing/reports/sales-trend', { params: { months } }).then((r) => r.data),
    invTopCustomers: (limit = 5) =>
        client.get('/invoicing/reports/top-customers', { params: { limit } }).then((r) => r.data),
    invTaxSummary: (period = 'mtd') =>
        client.get('/invoicing/reports/tax-summary', { params: { period } }).then((r) => r.data),
    invCollectionVelocity: () =>
        client.get('/invoicing/reports/collection-velocity').then((r) => r.data),
    invRecentPayments: (limit = 10) =>
        client.get('/invoicing/reports/recent-payments', { params: { limit } }).then((r) => r.data),
    invTopOverdue: (limit = 5) =>
        client.get('/invoicing/reports/top-overdue', { params: { limit } }).then((r) => r.data),

    // Settings
    invGetSettings: () => client.get('/invoicing/settings').then((r) => r.data),
    invUpdateSettings: (body) =>
        client.patch('/invoicing/settings', body).then((r) => r.data),

    // ---- Daily Sheet Scanner (gated by scanner allowlist, see /scanner/whoami) ----
    // Ported from the standalone Shakewallah Ledger app. See
    // /app/backend/daily_sheet.py for the extraction pipeline. All calls
    // will 403 for non-owner accounts, so the frontend also hides menu
    // items via the ownerFlag on `/auth/me` response.
    dsScan: (image_base64) =>
        client.post('/daily-sheet/scan', { image_base64 }).then((r) => r.data),
    dsPreviewPlan: (body) =>
        client.post('/daily-sheet/preview-plan', body).then((r) => r.data),
    dsSave: (body) =>
        client.post('/daily-sheet/save', body).then((r) => r.data),
    dsList: (limit = 60) =>
        client.get('/daily-sheet/list', { params: { limit } }).then((r) => r.data),
    dsDetail: (id) =>
        client.get(`/daily-sheet/${id}`).then((r) => r.data),
    dsDelete: (id, undoRouted = false) =>
        client
            .delete(`/daily-sheet/${id}`, { params: { undo_routed: undoRouted } })
            .then((r) => r.data),
    // Shakewallah-style 7-tab workbook. Pass EITHER `date` (YYYY-MM-DD) for
    // a single-day export OR `month` (YYYY-MM) for the whole month. Returns
    // { blob, filename } ready to be pipelined into an anchor download.
    dsExport: async ({ date, month }) => {
        const params = date ? { date } : { month };
        const r = await client.get('/daily-sheet/export', {
            params,
            responseType: 'blob',
        });
        // Prefer server-provided filename; fall back to a sensible default.
        const cd = r.headers?.['content-disposition'] || '';
        const match = cd.match(/filename="?([^";]+)"?/i);
        const filename = match ? match[1] : `DailySheet_${date || month}.xlsx`;
        return { blob: r.data, filename };
    },
    vrMonth: (month) =>
        client.get('/vijay-ras/month', { params: { month } }).then((r) => r.data),
    vrSettlement: (body) =>
        client.post('/vijay-ras/settlement', body).then((r) => r.data),

    // ─────────────────────────────────────────────────────────────
    // Sprint 2026-08 — Petpooja Recipe & Stock Management (Phase 7)
    // ─────────────────────────────────────────────────────────────
    // Curated UOM master (weight/volume/count/serving/container).
    // Cached client-side after first fetch since it's a static constant.
    stockUomMaster: () => client.get('/stock/uom-master').then((r) => r.data),

    // Raw materials — the "big list" auto-populated from vendor bills.
    stockRawMaterials: () => client.get('/stock/raw-materials').then((r) => r.data),
    setRawMaterialUom: (itemId, body) =>
        client.patch(`/stock/raw-materials/${encodeURIComponent(itemId)}`, body).then((r) => r.data),

    // Menu items (Petpooja side)
    listMenuItems: (params = {}) =>
        client.get('/menu-items', { params }).then((r) => r.data),
    listUnmappedMenuItems: () =>
        client.get('/menu-items/unmapped').then((r) => r.data),

    // Recipes (BOM)
    listRecipes: () => client.get('/recipes').then((r) => r.data),
    createRecipe: (body) => client.post('/recipes', body).then((r) => r.data),
    updateRecipe: (id, body) => client.patch(`/recipes/${id}`, body).then((r) => r.data),
    deleteRecipe: (id) => client.delete(`/recipes/${id}`).then((r) => r.data),

    // Stock ledger
    stockLevels: () => client.get('/stock/levels').then((r) => r.data),
    stockLedger: (params = {}) =>
        client.get('/stock/ledger', { params }).then((r) => r.data),
    stockAdjust: (body) => client.post('/stock/adjust', body).then((r) => r.data),

    // Petpooja sync
    petpoojaSync: (body) => client.post('/petpooja/sync', body).then((r) => r.data),
    listPetpoojaSyncs: (params = {}) =>
        client.get('/petpooja/syncs', { params }).then((r) => r.data),
    getPetpoojaSync: (id) =>
        client.get(`/petpooja/syncs/${id}`).then((r) => r.data),

    // Phase 7b — QSR loop completion
    // (a) Historical bill backfill (one-time, idempotent)
    stockBackfillFromBills: () =>
        client.post('/stock/backfill-from-bills').then((r) => r.data),
    // (b) Physical stock take + wastage
    stockTakePreview: () =>
        client.get('/stock/take-preview').then((r) => r.data),
    createStockTake: (body) =>
        client.post('/stock/take', body).then((r) => r.data),
    listStockTakes: (params = {}) =>
        client.get('/stock/takes', { params }).then((r) => r.data),
    // (c) Low-stock alerts
    stockLow: () => client.get('/stock/low').then((r) => r.data),

    // ── Phase 7c: QSR Profit Protection + Prep Engine ─────────────
    // Feature #1: dynamic food cost / margin erosion
    recipesMargins: () => client.get('/recipes/margins').then((r) => r.data),
    updateMenuItem: (id, body) =>
        client.patch(`/menu-items/${id}`, body).then((r) => r.data),
    // Feature #2: auto-86 reverse lookup
    menuItemsBlockedBy: (itemId) =>
        client.get(`/menu-items/blocked-by/${encodeURIComponent(itemId)}`).then((r) => r.data),
    // Feature #3: variance report
    stockVarianceReport: (params) =>
        client.get('/stock/variance-report', { params }).then((r) => r.data),
    // Feature #4: prep items + batches
    listPrepItems: () => client.get('/prep-items').then((r) => r.data),
    createPrepItem: (body) => client.post('/prep-items', body).then((r) => r.data),
    deletePrepItem: (id, { force = false } = {}) =>
        client.delete(`/prep-items/${encodeURIComponent(id)}`, { params: force ? { force: 'true' } : {} })
              .then((r) => r.data),
    logPrepBatch: (body) => client.post('/prep-batches', body).then((r) => r.data),
    listPrepBatches: (params = {}) =>
        client.get('/prep-batches', { params }).then((r) => r.data),

    // Sprint 2026-08 · Layers 2 & 3 — Raw material de-duplication
    mergeRawMaterials: (body) =>
        client.post('/stock/raw-materials/merge', body).then((r) => r.data),
    clusterSuggestions: () =>
        client.post('/stock/raw-materials/cluster-suggestions').then((r) => r.data),

    // ── Phase 7e: Production Runs + Finished Goods Lots ───────────
    processBatch: (body) =>
        client.post('/production/process-batch', body).then((r) => r.data),
    listProductionRuns: (params = {}) =>
        client.get('/production/runs', { params }).then((r) => r.data),
    listFinishedGoodsLots: (params = {}) =>
        client.get('/production/lots', { params }).then((r) => r.data),
    scanLot: (barcode) =>
        client.get(`/production/lots/scan/${encodeURIComponent(barcode)}`).then((r) => r.data),
    adjustLot: (lotId, body) =>
        client.post(`/production/lots/${encodeURIComponent(lotId)}/adjust`, body).then((r) => r.data),

    // ── Phase 8: Accounting Vouchers (Daily Sheet → Section 40A(3)) ──
    previewVouchers: (sheetId) =>
        client.post(`/daily-sheet/${encodeURIComponent(sheetId)}/preview-vouchers`).then((r) => r.data),
    commitVouchers: (sheetId, decisions = []) =>
        client.post(`/daily-sheet/${encodeURIComponent(sheetId)}/commit-vouchers`, { decisions })
              .then((r) => r.data),
    listVouchers: (params = {}) =>
        client.get('/vouchers', { params }).then((r) => r.data),
    section40a3Report: (params = {}) =>
        client.get('/vouchers/section-40a3-report', { params }).then((r) => r.data),
    getItemLastPrices: () =>
        client.get('/vouchers/item-last-prices').then((r) => r.data),
    backfillVouchersFromDailySheets: (params = {}) =>
        client.post('/vouchers/backfill-from-daily-sheets', null, { params, timeout: 180000 })
              .then((r) => r.data),
    renumberVouchers: () =>
        client.post('/vouchers/renumber-all').then((r) => r.data),
    deleteVoucher: (voucherId) =>
        client.delete(`/vouchers/${encodeURIComponent(voucherId)}`).then((r) => r.data),
    bulkDeleteVouchers: (ids) =>
        client.post('/vouchers/bulk-delete', { ids }).then((r) => r.data),
    // Detail payload used by the Voucher preview dialog (Tally-style).
    // Returns everything on the doc PLUS pre-computed debit_ledger,
    // credit_ledger, title, amount_in_words, source_sheet, company_name.
    getVoucher: (voucherId) =>
        client.get(`/vouchers/${encodeURIComponent(voucherId)}`).then((r) => r.data),
    // Fetches the formal HTML slip through the authed axios client, then
    // opens it in a new window and triggers Print. Auth-gated endpoints
    // can't be hit via <a href> or window.open() (no JWT), so we go via
    // XHR → document.write.
    printVoucher: async (voucherId) => {
        const r = await client.get(
            `/vouchers/${encodeURIComponent(voucherId)}/print`,
            { responseType: 'text', transformResponse: [(x) => x] },
        );
        const html = r.data;
        const w = window.open('', '_blank', 'width=900,height=1000');
        if (!w) {
            throw new Error('Popup blocked. Please allow popups for this site.');
        }
        w.document.open();
        w.document.write(html);
        w.document.close();
        // Kick the print dialog once the new window finishes laying out.
        // Short delay so styles apply and images (if any) load first.
        setTimeout(() => {
            try { w.focus(); w.print(); } catch (e) { /* user closed the tab */ }
        }, 350);
        return { ok: true };
    },
    // Bulk print — fetches a single combined HTML doc for the current
    // filter (one voucher per A4 page) and pops the print dialog. User
    // then Save-as-PDFs → one file for the whole batch.
    printVoucherBatch: async (params = {}) => {
        const r = await client.get('/vouchers/print-batch', {
            params,
            responseType: 'text',
            transformResponse: [(x) => x],
            // Bulk exports can take longer for big filters; give the browser
            // enough time.
            timeout: 60000,
        });
        const count = Number(r.headers?.['x-voucher-count'] || 0);
        const html = r.data;
        const w = window.open('', '_blank', 'width=900,height=1000');
        if (!w) {
            throw new Error('Popup blocked. Please allow popups for this site.');
        }
        w.document.open();
        w.document.write(html);
        w.document.close();
        setTimeout(() => {
            try { w.focus(); w.print(); } catch (e) { /* user closed the tab */ }
        }, 400);
        return { ok: true, count };
    },
    // Bulk CSV download — returns raw text; frontend triggers file save.
    downloadVouchersCsv: (params = {}) =>
        client.get('/vouchers/export.csv', { params, responseType: 'blob' }),
    // Tally XML export — CA imports directly into TallyPrime.
    downloadVouchersTallyXml: (params = {}) =>
        client.get('/vouchers/export.tally.xml', { params, responseType: 'blob' }),
};

/**
 * Format a numeric amount into a currency-prefixed string.
 * - `null`/`undefined`/`NaN` → em-dash
 * - Known ISO currency codes → symbol (INR → ₹, USD → $, EUR → €, GBP → £, JPY → ¥, AED → د.إ)
 * - Unknown / empty currency → default to ₹ (Indian Rupee) — this is a
 *   B2B SaaS built for the Indian market. Previously the code would echo
 *   the raw currency string (e.g. "INR 1,000.00"), which looked like a
 *   dollar-sign to some users and was inconsistent with the rest of the
 *   product. 2026-01: consolidated to always render the ₹ symbol unless
 *   the bill was explicitly in another known currency.
 */
const _CURRENCY_SYMBOLS = {
    INR: '₹',
    RS: '₹',
    RUP: '₹',
    RUPEES: '₹',
    USD: '$',
    EUR: '€',
    GBP: '£',
    JPY: '¥',
    AED: 'د.إ',
    CAD: 'C$',
    AUD: 'A$',
};

export function fmtMoney(n, currency) {
    if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
    const val = Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    let sym = '₹';
    if (currency) {
        const key = String(currency).trim().toUpperCase();
        // If caller already passed a symbol (₹, $, €, £, ¥), use it as-is.
        if (['₹', '$', '€', '£', '¥'].includes(currency)) {
            sym = currency;
        } else if (_CURRENCY_SYMBOLS[key]) {
            sym = _CURRENCY_SYMBOLS[key];
        }
        // If the caller passed something we don't recognise (e.g. a code we
        // haven't mapped), fall back to the ₹ default rather than echoing
        // the raw code — keeps the UI consistent.
    }
    return `${sym} ${val}`;
}

/**
 * Smart quantity formatter.
 * - null / undefined / NaN            -> em-dash
 * - values within 0.001 of an integer -> displayed as integer (e.g. 5.0 -> "5")
 * - genuine fractional quantities     -> up to 3 decimals, trailing zeros stripped
 *   (so weight/volume items like 1.5 kg stay as "1.5", not "1.50")
 *
 * @param {number|string|null|undefined} n
 * @param {{dash?: string}} [opts]
 * @returns {string}
 */
export function fmtQty(n, opts = {}) {
    const dash = opts.dash ?? '—';
    if (n === null || n === undefined) return dash;
    const num = Number(n);
    if (Number.isNaN(num)) return dash;
    // Treat 0 as em-dash for aggregated views (mirrors previous behavior).
    // Callers wanting a literal "0" should pass { dash: '0' }.
    if (num === 0) return dash;
    // Whole-number tolerance (float summation may leave 4.9999999 or 5.000001)
    if (Math.abs(num - Math.round(num)) < 0.001) {
        return String(Math.round(num));
    }
    // Fractional — keep up to 3 decimals, strip trailing zeros for cleanliness
    return num.toFixed(3).replace(/\.?0+$/, '');
}

export function fmtDelta(delta) {
    if (delta === null || delta === undefined) return '—';
    const sign = delta > 0 ? '+' : delta < 0 ? '' : '';
    return `${sign}${Number(delta).toFixed(2)}`;
}

export function fmtPercent(p) {
    if (p === null || p === undefined) return '—';
    const sign = p > 0 ? '+' : '';
    return `${sign}${Number(p).toFixed(2)}%`;
}

export function fmtDate(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(iso.length === 10 ? iso + 'T00:00:00Z' : iso);
        return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
    } catch {
        return iso;
    }
}

export function fmtMonth(ym) {
    if (!ym) return '—';
    // ym is YYYY-MM
    try {
        const [y, m] = ym.split('-').map((x) => Number(x));
        const d = new Date(Date.UTC(y, (m || 1) - 1, 1));
        return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    } catch {
        return ym;
    }
}

export function fmtDateTime(iso) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch {
        return iso;
    }
}

/**
 * Build a UPI intent URL (upi://pay?...) per NPCI spec. Works with any UPI app
 * (GPay, PhonePe, Paytm, BHIM). Amount is INR by default.
 *
 * @param {object} opts
 * @param {string} opts.pa   payee VPA (e.g. "vendor@upi") — required
 * @param {string} [opts.pn] payee name shown in the UPI app
 * @param {number} [opts.am] transaction amount
 * @param {string} [opts.tn] transaction note
 * @param {string} [opts.tr] merchant transaction reference id (optional)
 */
export function buildUpiIntentUrl({ pa, pn, am, tn, tr } = {}) {
    if (!pa) return null;
    const params = new URLSearchParams();
    params.set('pa', pa);
    if (pn) params.set('pn', pn);
    if (am !== undefined && am !== null && !Number.isNaN(Number(am))) {
        params.set('am', Number(am).toFixed(2));
    }
    params.set('cu', 'INR');
    if (tn) params.set('tn', tn);
    if (tr) params.set('tr', tr);
    // Some apps prefer '&' over URLSearchParams encoding (spaces -> +). Manually rebuild.
    const encoded = [];
    params.forEach((v, k) => encoded.push(`${k}=${encodeURIComponent(v).replace(/%20/g, '%20')}`));
    return `upi://pay?${encoded.join('&')}`;
}
